import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import Header from "./Header";
import VertNav from "./VertNav";
import config from "../../config";

const StaffSetting = () => {
    const token = localStorage.getItem("token");
    const bid = localStorage.getItem("branch_id");

    const [monthDays, setMonthDays] = useState({
        Jan: 0,
        Feb: 0,
        Mar: 0,
        Apr: 0,
        May: 0,
        Jun: 0,
        Jul: 0,
        Aug: 0,
        Sep: 0,
        Oct: 0,
        Nov: 0,
        Dec: 0,
    });

    const [attendanceTimings, setAttendanceTimings] = useState({
        inTime: localStorage.getItem("in_time") || "09:00",
        outTime: localStorage.getItem("out_time") || "17:00",
    });

    const [workingHours, setWorkingHours] = useState("8 Hours");
    const [isLoading, setIsLoading] = useState(true);

    const monthNames = {
        Jan: "January",
        Feb: "February",
        Mar: "March",
        Apr: "April",
        May: "May",
        Jun: "June",
        Jul: "July",
        Aug: "August",
        Sep: "September",
        Oct: "October",
        Nov: "November",
        Dec: "December",
    };

    const monthToNumber = {
        Jan: 1,
        Feb: 2,
        Mar: 3,
        Apr: 4,
        May: 5,
        Jun: 6,
        Jul: 7,
        Aug: 8,
        Sep: 9,
        Oct: 10,
        Nov: 11,
        Dec: 12,
    };

    /* ================= FETCH SETTINGS ================= */
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);

                const response = await fetch(
                    `${config.apiUrl}/api/swalook/staff/setting/?branch_name=${bid}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Token ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();

                if (data.status && data.table_data) {
                    const initialMonthDays = {
                        Jan: 0,
                        Feb: 0,
                        Mar: 0,
                        Apr: 0,
                        May: 0,
                        Jun: 0,
                        Jul: 0,
                        Aug: 0,
                        Sep: 0,
                        Oct: 0,
                        Nov: 0,
                        Dec: 0,
                    };

                    data.table_data.forEach((item) => {
                        const monthKey = Object.keys(monthToNumber).find(
                            (key) => monthToNumber[key] === item.month
                        );
                        if (monthKey) {
                            initialMonthDays[monthKey] =
                                item.number_of_working_days;
                        }
                    });

                    setMonthDays(initialMonthDays);

                    if (data.attendance_timings) {
                        const { in_time, out_time } = data.attendance_timings;

                        setAttendanceTimings({
                            inTime: in_time,
                            outTime: out_time,
                        });

                        calculateWorkingHours(in_time, out_time);

                        localStorage.setItem("in_time", in_time);
                        localStorage.setItem("out_time", out_time);
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [bid, token]);

    /* ================= UTILS ================= */
    const calculateWorkingHours = (inTime, outTime) => {
        if (!inTime || !outTime) {
            setWorkingHours("0 Hours");
            return;
        }

        const [inHours, inMinutes] = inTime.split(":").map(Number);
        const [outHours, outMinutes] = outTime.split(":").map(Number);

        let totalHours = outHours - inHours;
        let totalMinutes = outMinutes - inMinutes;

        if (totalMinutes < 0) {
            totalHours -= 1;
            totalMinutes += 60;
        }

        setWorkingHours(`${totalHours}.${totalMinutes} Hours`);
    };

    const handleTimeChange = (e) => {
        const { name, value } = e.target;

        setAttendanceTimings((prev) => {
            const updated = { ...prev, [name]: value };
            calculateWorkingHours(updated.inTime, updated.outTime);
            return updated;
        });
    };

    const handleMonthDaysChange = (month, newValue) => {
        setMonthDays((prev) => ({
            ...prev,
            [month]: parseInt(newValue) || 0,
        }));
    };

    /* ================= SAVE SETTINGS ================= */
    const handleSaveSettings = async () => {
        try {
            setIsLoading(true);

            const json_data = {};
            Object.keys(monthDays).forEach((monthKey) => {
                json_data[monthToNumber[monthKey]] = String(
                    monthDays[monthKey]
                );
            });

            const response = await fetch(
                `${config.apiUrl}/api/swalook/staff/setting/?branch_name=${bid}`,
                {
                    method: "POST",
                    headers: {
                        Authorization: `Token ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        in_time: attendanceTimings.inTime,
                        out_time: attendanceTimings.outTime,
                        json_data,
                    }),
                }
            );

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();
            console.log("SAVE RESPONSE 👉", result);

            if (result.status) {
                const { in_time, out_time } = result.data;

                setAttendanceTimings({
                    inTime: in_time,
                    outTime: out_time,
                });

                localStorage.setItem("in_time", in_time);
                localStorage.setItem("out_time", out_time);

                calculateWorkingHours(in_time, out_time);
            }
        } catch (error) {
            console.error("Error while saving settings:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <div className="min-h-screen bg-gray-50">
                <Header />
                <VertNav />
                <Helmet>
                    <title>Staff Settings</title>
                </Helmet>
                <div className="p-4 md:p-8 md:ml-72">
                    <div className="bg-white shadow-xl rounded-3xl p-6 md:p-8 mx-auto">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-6 text-center">
                            Staff Settings
                        </h2>

                        {isLoading ? (
                            <div className="flex justify-center items-center py-12">
                                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                            </div>
                        ) : (
                            <>
                                {/* Attendance Timings Section */}
                                <div className="mb-8 bg-gray-50 p-6 rounded-2xl">
                                    <h3 className="text-lg md:text-xl font-semibold text-gray-700 mb-4">
                                        Attendance Timings
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
                                        <div className="space-y-1">
                                            <label className="block text-sm font-medium text-gray-600">
                                                In-time
                                            </label>
                                            <input
                                                type="time"
                                                name="inTime"
                                                value={attendanceTimings.inTime}
                                                onChange={handleTimeChange}
                                                className="w-full border border-gray-300 rounded-full px-4 py-2"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="block text-sm font-medium text-gray-600">
                                                Out-time
                                            </label>
                                            <input
                                                type="time"
                                                name="outTime"
                                                value={
                                                    attendanceTimings.outTime
                                                }
                                                onChange={handleTimeChange}
                                                className="w-full border border-gray-300 rounded-full px-4 py-2"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="block text-sm font-medium text-gray-600">
                                                Working Hours
                                            </label>
                                            <div className="bg-gray-100 px-4 py-2 rounded-full text-gray-800 font-medium text-center">
                                                {workingHours}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Month Days Section */}
                                <div className="mb-8">
                                    <h3 className="text-lg md:text-xl font-semibold text-gray-700 mb-4">
                                        Number of Days in Each Month
                                    </h3>
                                    <div className="overflow-hidden rounded-2xl border border-gray-200 shadow-sm">
                                        <table className="w-full">
                                            <thead>
                                                <tr className="bg-gray-50">
                                                    <th className="px-6 py-3 text-center text-lg font-semibold uppercase tracking-wider">
                                                        Month
                                                    </th>
                                                    <th className="px-6 py-3 text-center text-lg font-semibold uppercase tracking-wider">
                                                        Days
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {Object.keys(monthNames).map(
                                                    (month) => (
                                                        <tr key={month}>
                                                            <td className="py-2 px-4 border">
                                                                {
                                                                    monthNames[
                                                                        month
                                                                    ]
                                                                }
                                                            </td>
                                                            <td className="py-2 px-4 border">
                                                                <input
                                                                    type="number"
                                                                    min="1"
                                                                    max="31"
                                                                    value={
                                                                        monthDays[
                                                                            month
                                                                        ] || ""
                                                                    }
                                                                    onChange={(
                                                                        e
                                                                    ) =>
                                                                        handleMonthDaysChange(
                                                                            month,
                                                                            e
                                                                                .target
                                                                                .value
                                                                        )
                                                                    }
                                                                    className="w-20 border rounded px-2 py-1"
                                                                />
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Save Button */}
                                <div className="flex justify-center">
                                    <button
                                        onClick={handleSaveSettings}
                                        disabled={isLoading}
                                        className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-8 rounded-full"
                                    >
                                        {isLoading
                                            ? "Saving..."
                                            : "Save Settings"}
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

export default StaffSetting;
